import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Slider,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Collapse,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Psychology as PsychologyIcon,
  TrendingUp as ImproveIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AutoAwesomeIcon,
  Timeline as TimelineIcon,
  Analytics as AnalyticsIcon,
  Architecture as StructureIcon,
  Brush as CreativeIcon,
  RateReview as CriticIcon,
  FolderOutlined as SessionsIcon,
  PlayArrowOutlined as CurrentIcon,
} from '@mui/icons-material';
import { 
  PromptImprovementSession,
  PromptRequest,
  PromptIterationDisplay
} from '../models/promptImprovement';
import { PromptImprovementService } from '../services/promptImprovementService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';

const PromptImprovement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [sessions, setSessions] = useState<PromptImprovementSession[]>([]);
  const [currentSession, setCurrentSession] = useState<PromptImprovementSession | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [startingPrompt, setStartingPrompt] = useState('');
  const [iterations, setIterations] = useState(2);
  const [maxTokens, setMaxTokens] = useState(512);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIteration, setSelectedIteration] = useState<number | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [iterateMode, setIterateMode] = useState(false);
  const [customEndpoint, setCustomEndpoint] = useState('');
  const [iterateRequest, setIterateRequest] = useState('{}');
  const [showIterateDialog, setShowIterateDialog] = useState(false);
  const [selectedAgentType, setSelectedAgentType] = useState<'Expander' | 'Structurer' | 'Creative' | 'Critic'>('Expander');
  const [iterateInstructions, setIterateInstructions] = useState('');
  const [iterateTemperature, setIterateTemperature] = useState(0.7);

  // Backend asset retrieval
  const {
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'PromptImprovement',
    sessionBased: false,
    autoFetch: true,
  });

  useEffect(() => {
    // Load sessions from localStorage on mount
    const loadedSessions = PromptImprovementService.loadSessions();
    setSessions(loadedSessions);
    if (loadedSessions.length > 0) {
      setCurrentSession(loadedSessions[0]);
      setActiveTab(2); // Switch to Current Session tab
    }
  }, []);

  // Merge backend assets with local data
  useEffect(() => {
    const localData = PromptImprovementService.loadSessions();

    if (backendAssets && backendAssets.length > 0) {
      const converted: PromptImprovementSession[] = backendAssets.map(asset => {
        const sessionData = asset.textContent ? JSON.parse(asset.textContent) : null;
        return {
          id: asset.id,
          sessionId: asset.id,
          startingPrompt: sessionData?.startingPrompt || '',
          promptIterations: sessionData?.promptIterations || [],
          createdAt: new Date(asset.createdAt),
          lastUpdatedAt: new Date(asset.updatedAt || asset.createdAt),
          agentEndpoint: sessionData?.agentEndpoint,
          iterateMode: sessionData?.iterateMode
        };
      });

      const backendIds = new Set(converted.map(s => s.id));
      const localOnly = localData.filter(s => !backendIds.has(s.id));
      setSessions([...converted, ...localOnly]);
    }
  }, [backendAssets]);

  const handleStartNewSession = async () => {
    if (!startingPrompt.trim()) {
      setError('Please enter a starting prompt');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request: PromptRequest = {
        startingPrompt: startingPrompt.trim(),
        iterations,
        maxTokensPerIteration: maxTokens
      };

      const response = await PromptImprovementService.startSession(request);
      
      // Fetch the full session data
      const fullSession = await PromptImprovementService.getSession(response.sessionId);
      
      // Save to local storage
      PromptImprovementService.saveSession(fullSession);
      
      // Update state
      setSessions(prev => [fullSession, ...prev]);
      setCurrentSession(fullSession);
      setStartingPrompt('');
      setSelectedIteration(fullSession.promptIterations.length - 1);
      setActiveTab(2); // Switch to Current Session tab
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start prompt improvement session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    PromptImprovementService.deleteSession(sessionId);
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    
    if (currentSession?.id === sessionId) {
      const remainingSessions = sessions.filter(s => s.id !== sessionId);
      setCurrentSession(remainingSessions.length > 0 ? remainingSessions[0] : null);
      if (remainingSessions.length === 0) {
        setActiveTab(0); // Go to New Session tab if no sessions left
      }
    }
  };

  const handleExportSession = async () => {
    if (currentSession) {
      try {
        // Try to use API export first
        await PromptImprovementService.downloadSession(currentSession.id);
      } catch (err) {
        // Fallback to local export if API fails
        console.warn('API export failed, using local export:', err);
        PromptImprovementService.exportSessionLocal(currentSession);
      }
    }
  };

  const handleImportSession = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const importedSession = await PromptImprovementService.importSession(file);
      setSessions(prev => [importedSession, ...prev]);
      setCurrentSession(importedSession);
    } catch (err) {
      setError('Failed to import session');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDeleteIteration = (index: number) => {
    if (!currentSession || currentSession.promptIterations.length <= 1) {
      setError('Cannot delete the last remaining iteration');
      return;
    }

    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this iteration?')) {
      return;
    }

    // Remove the iteration
    currentSession.promptIterations.splice(index, 1);
    currentSession.lastUpdatedAt = new Date();
    
    // Save updated session
    PromptImprovementService.saveSession(currentSession);
    setCurrentSession({ ...currentSession });
    
    // Adjust selected iteration if needed
    if (selectedIteration !== null && selectedIteration >= index) {
      setSelectedIteration(Math.max(0, selectedIteration - 1));
    }
  };

  const handleIterate = async () => {
    if (!currentSession || !currentSession.sessionId || !currentSession.agentEndpoint) {
      setError('Session not configured for iteration mode');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let requestObj = {};
      try {
        requestObj = JSON.parse(iterateRequest);
      } catch (e) {
        // If not valid JSON, treat as simple feedback string
        requestObj = { feedback: iterateRequest };
      }

      const result = await PromptImprovementService.iterate(
        currentSession.sessionId,
        currentSession.agentEndpoint,
        requestObj
      );

      // Add the enhanced prompt to iterations
      if (result.enhancedPrompt) {
        currentSession.promptIterations.push(result.enhancedPrompt);
        currentSession.lastUpdatedAt = new Date();
        
        // Save updated session
        PromptImprovementService.saveSession(currentSession);
        setCurrentSession({ ...currentSession });
        setSelectedIteration(currentSession.promptIterations.length - 1);
      } else {
        setError('No enhanced prompt returned from agent');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to iterate');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromptImproveIterate = async () => {
    if (!currentSession || !currentSession.sessionId) {
      setError('No active session');
      return;
    }

    setIsLoading(true);
    setError(null);
    setShowIterateDialog(false);

    try {
      // Get the current prompt (last iteration)
      const currentPrompt = currentSession.promptIterations[currentSession.promptIterations.length - 1];
      
      // Call the prompt improvement iterate endpoint
      const endpoint = `/api/agent/prompt/${currentSession.sessionId}/iterate`;
      const response = await PromptImprovementService.iterate(
        currentSession.sessionId,
        endpoint,
        {
          agentType: selectedAgentType,
          additionalInstructions: iterateInstructions,
          temperature: iterateTemperature,
          prompt: currentPrompt
        }
      );

      // Add the enhanced prompt to iterations
      if (response.enhancedPrompt) {
        currentSession.promptIterations.push(response.enhancedPrompt);
        currentSession.lastUpdatedAt = new Date();
        
        // Save updated session
        PromptImprovementService.saveSession(currentSession);
        setCurrentSession({ ...currentSession });
        setSelectedIteration(currentSession.promptIterations.length - 1);
        setIterateInstructions(''); // Clear instructions
      } else {
        setError('No enhanced prompt returned from agent');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to iterate prompt');
    } finally {
      setIsLoading(false);
    }
  };

  const exportSinglePrompt = (prompt: string, iterationIndex: number) => {
    const data = {
      prompt,
      iterationNumber: iterationIndex,
      isImprovement: iterationIndex > 0,
      exportedAt: new Date().toISOString(),
      sessionId: currentSession?.id
    };
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `prompt-${iterationIndex === 0 ? 'original' : `improvement-${iterationIndex}`}-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const getIterationDisplay = (): PromptIterationDisplay[] => {
    if (!currentSession) return [];
    
    return currentSession.promptIterations.map((prompt, index) => ({
      iterationNumber: index,
      prompt,
      isImprovement: index > 0
    }));
  };

  const renderAnalysis = () => {
    if (!currentSession || currentSession.promptIterations.length < 2) return null;

    const analysis = PromptImprovementService.analyzeImprovements(currentSession.promptIterations);

    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnalyticsIcon /> Improvement Analysis
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Iterations</Typography>
              <Typography variant="h4">{analysis.totalIterations}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Average Length</Typography>
              <Typography variant="h4">{analysis.averageLength}</Typography>
              <Typography variant="caption" color="text.secondary">characters</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Final Length</Typography>
              <Typography variant="h4">{analysis.lengthProgression[analysis.lengthProgression.length - 1]}</Typography>
              <Typography variant="caption" color="text.secondary">characters</Typography>
            </Box>
          </Box>
          <Typography variant="subtitle2" gutterBottom>Length Progression</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {analysis.lengthProgression.map((length, index) => (
              <Chip
                key={index}
                label={`v${index}: ${length}`}
                size="small"
                color={index === selectedIteration ? 'primary' : 'default'}
                onClick={() => setSelectedIteration(index)}
              />
            ))}
          </Box>
          <Typography variant="subtitle2" gutterBottom>Improvements</Typography>
          <List dense>
            {analysis.improvements.map((improvement, index) => (
              <ListItem key={index}>
                <ListItemText 
                  primary={improvement}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSelectSession = (session: PromptImprovementSession) => {
    setCurrentSession(session);
    setSelectedIteration(session.promptIterations.length - 1);
    setActiveTab(2); // Switch to Current Session tab
  };

  const renderNewSessionTab = () => (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' }
          }}
        >
          {!isMobile && <AddIcon />}
          {isMobile ? 'New Session' : 'Start New Prompt Improvement Session'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ 
            mb: { xs: 2, sm: 3, md: 4 },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          {isMobile ? 'Improve prompts with AI' : 'Create a new session to iteratively improve your prompts using AI assistance.'}
        </Typography>
        
        <TextField
          fullWidth
          multiline
          rows={isMobile ? 4 : 4}
          label="Starting Prompt"
          value={startingPrompt}
          onChange={(e) => setStartingPrompt(e.target.value)}
          placeholder={isMobile ? "Enter prompt..." : "Enter the prompt you want to improve..."}
          sx={{ mb: 3 }}
          InputProps={{
            sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
          }}
          InputLabelProps={{
            sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
          }}
        />
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <Typography 
            gutterBottom
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '0.95rem' },
              fontWeight: 600
            }}
          >
            {isMobile ? 'Iterations' : 'Number of Iterations'}: {iterations}
          </Typography>
          <Slider
            value={iterations}
            onChange={(_, value) => setIterations(value as number)}
            min={1}
            max={3}
            marks
            valueLabelDisplay="auto"
          />
          <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {isMobile ? 'Each iteration refines prompt' : 'Each iteration will refine and improve the prompt'}
          </FormHelperText>
        </FormControl>

        <FormControl fullWidth sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <Typography 
            gutterBottom
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '0.95rem' },
              fontWeight: 600
            }}
          >
            {isMobile ? 'Max Tokens' : 'Max Tokens per Iteration'}: {maxTokens}
          </Typography>
          <Slider
            value={maxTokens}
            onChange={(_, value) => setMaxTokens(value as number)}
            min={256}
            max={2048}
            step={256}
            marks={[
              { value: 256, label: '256' },
              { value: 512, label: '512' },
              { value: 1024, label: '1K' },
              { value: 2048, label: '2K' },
            ]}
            valueLabelDisplay="auto"
          />
        </FormControl>

        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={handleStartNewSession}
          disabled={isLoading || !startingPrompt.trim()}
          startIcon={isLoading ? <CircularProgress size={20} /> : (!isMobile && <AutoAwesomeIcon />)}
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '1rem' },
            minHeight: 44
          }}
        >
          {isLoading ? 'Improving...' : (isMobile ? 'Start' : 'Start Improvement')}
        </Button>
      </Paper>
    </Box>
  );

  const renderSessionsTab = () => (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h5"
          sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
        >
          {isMobile ? `Sessions (${sessions.length})` : `Prompt Sessions (${sessions.length})`}
        </Typography>
        <Box>
          <Tooltip title={isMobile ? "" : "Import Session"}>
            <IconButton 
              component="label"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <UploadIcon />
              <input
                type="file"
                hidden
                accept=".json"
                onChange={handleImportSession}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {sessions.length === 0 ? (
        <Paper sx={{ p: { xs: 3, sm: 4, md: 6 }, textAlign: 'center' }}>
          <PsychologyIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
          >
            {isMobile ? 'No Sessions' : 'No Sessions Yet'}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {isMobile ? 'Create your first session' : 'Create your first prompt improvement session to get started'}
          </Typography>
          <Button
            variant="contained"
            startIcon={!isMobile && <AddIcon />}
            onClick={() => setActiveTab(0)}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44
            }}
          >
            {isMobile ? 'Create' : 'Create Session'}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {sessions.map(session => (
            <Grid item xs={12} md={6} key={session.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { elevation: 4 },
                  border: currentSession?.id === session.id ? 2 : 0,
                  borderColor: 'primary.main'
                }}
                onClick={() => handleSelectSession(session)}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PsychologyIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        flexGrow: 1,
                        fontSize: { xs: '0.95rem', sm: '1.125rem' }
                      }}
                    >
                      {isMobile && session.startingPrompt.length > 30
                        ? session.startingPrompt.substring(0, 30) + '...'
                        : session.startingPrompt.substring(0, 40) + '...'
                      }
                    </Typography>
                    {currentSession?.id === session.id && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="primary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    )}
                  </Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '0.875rem', sm: '0.95rem' }
                    }}
                  >
                    {session.promptIterations.length} iterations
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  >
                    Created: {new Date(session.createdAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={!isMobile && <CurrentIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectSession(session);
                    }}
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                      minHeight: 36
                    }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    startIcon={!isMobile && <DownloadIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentSession(session);
                      handleExportSession();
                    }}
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                      minHeight: 36
                    }}
                  >
                    Export
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={!isMobile && <DeleteIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this session?')) {
                        handleDeleteSession(session.id);
                      }
                    }}
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                      minHeight: 36
                    }}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const renderCurrentSessionTab = () => {
    if (!currentSession) {
      return (
        <Paper sx={{ p: { xs: 3, sm: 4, md: 6 }, textAlign: 'center' }}>
          <PsychologyIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.secondary', mb: 2 }} />
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
          >
            {isMobile ? 'No Session' : 'No Session Selected'}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {isMobile ? 'Create or select a session' : 'Create a new session or select an existing one to view details'}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2, 
            justifyContent: 'center' 
          }}>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={() => setActiveTab(0)}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44
              }}
            >
              New Session
            </Button>
            <Button
              variant="outlined"
              startIcon={!isMobile && <SessionsIcon />}
              onClick={() => setActiveTab(1)}
              disabled={sessions.length === 0}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44
              }}
            >
              {isMobile ? 'Browse' : 'Browse Sessions'}
            </Button>
          </Box>
        </Paper>
      );
    }

    return (
      <Box>
        {/* Session Info */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'flex-start' }, 
            mb: 2,
            gap: { xs: 1, sm: 0 }
          }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
            >
              {isMobile ? 'Details' : 'Session Details'}
            </Typography>
            <Tooltip title={isMobile ? "" : "Export Session"}>
              <IconButton 
                onClick={handleExportSession}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' }, 
            gap: 2 
          }}>
            <Box>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Created
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {isMobile 
                  ? new Date(currentSession.createdAt).toLocaleDateString()
                  : new Date(currentSession.createdAt).toLocaleString()
                }
              </Typography>
            </Box>
            <Box>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Last Updated
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {isMobile 
                  ? new Date(currentSession.lastUpdatedAt).toLocaleDateString()
                  : new Date(currentSession.lastUpdatedAt).toLocaleString()
                }
              </Typography>
            </Box>
            <Box>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Total Iterations
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {currentSession.promptIterations.length}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Iterate Controls - Show only in iterate mode */}
        {currentSession.iterateMode && (
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: { xs: '1rem', sm: '1.125rem' }
              }}
            >
              {!isMobile && <RefreshIcon />} 
              Iterate Mode
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
              >
                {isMobile ? 'Endpoint' : 'Endpoint'}: {currentSession.agentEndpoint || 'Not configured'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
              >
                {isMobile ? 'ID' : 'Session ID'}: {currentSession.sessionId || 'Not configured'}
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Iteration Request (JSON or text feedback)"
              value={iterateRequest}
              onChange={(e) => setIterateRequest(e.target.value)}
              placeholder='{"feedback": "enhance visual storytelling", "panelRequest": "dynamic composition"}'
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleIterate}
              disabled={isLoading || !currentSession.sessionId || !currentSession.agentEndpoint}
              startIcon={isLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
            >
              {isLoading ? 'Iterating...' : 'Generate Next Iteration'}
            </Button>
          </Paper>
        )}

        {/* Iterations Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon /> Prompt Evolution
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!currentSession.iterateMode && currentSession.promptIterations.length > 0 && (
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={() => setShowIterateDialog(true)}
                  size="small"
                >
                  Iterate
                </Button>
              )}
              <Button
                startIcon={showAnalysis ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowAnalysis(!showAnalysis)}
                size="small"
              >
                {showAnalysis ? 'Hide' : 'Show'} Analysis
              </Button>
            </Box>
          </Box>

          <Collapse in={showAnalysis}>
            {renderAnalysis()}
          </Collapse>

          <Stepper activeStep={selectedIteration ?? 0} orientation="vertical" sx={{ mt: 3 }}>
            {getIterationDisplay().map((iteration, index) => (
              <Step key={index} expanded>
                <StepLabel
                  onClick={() => setSelectedIteration(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {index === 0 ? 'Original Prompt' : `Improvement ${index}`}
                    {index === currentSession.promptIterations.length - 1 && (
                      <Chip label="Latest" size="small" color="primary" />
                    )}
                  </Box>
                </StepLabel>
                <StepContent>
                  <Paper 
                    elevation={selectedIteration === index ? 3 : 1}
                    sx={{ 
                      p: 2, 
                      bgcolor: selectedIteration === index ? 'action.selected' : 'background.paper',
                      border: selectedIteration === index ? 2 : 0,
                      borderColor: 'primary.main'
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {iteration.prompt}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {iteration.prompt.length} characters
                      </Typography>
                      <Box>
                        <Tooltip title="Export prompt">
                          <IconButton 
                            size="small" 
                            onClick={() => exportSinglePrompt(iteration.prompt, index)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy to clipboard">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(iteration.prompt)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {currentSession.promptIterations.length > 1 && (
                          <Tooltip title="Delete iteration">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteIteration(index);
                              }}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <PsychologyIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Prompt Studio' : 'Prompt Improvement Studio'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'Improve prompts with AI' : 'Iteratively improve your prompts using AI-powered enhancement agents'}
        </Typography>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)} 
          sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {error}
        </Alert>
      )}

      {/* Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant={isMobile ? "fullWidth" : "fullWidth"}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={!isTablet ? <AddIcon /> : undefined} 
            label={isMobile ? "New" : "New Session"} 
            sx={{ 
              textTransform: 'none',
              minHeight: 48,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          />
          <Tab 
            icon={!isTablet ? <SessionsIcon /> : undefined} 
            label={isMobile ? `(${sessions.length})` : `Sessions (${sessions.length})`} 
            sx={{ 
              textTransform: 'none',
              minHeight: 48,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          />
          <Tab 
            icon={!isTablet ? <CurrentIcon /> : undefined} 
            label={isMobile ? "Current" : "Current Session"} 
            sx={{ 
              textTransform: 'none',
              minHeight: 48,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
            disabled={!currentSession}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && renderNewSessionTab()}
        {activeTab === 1 && renderSessionsTab()}
        {activeTab === 2 && renderCurrentSessionTab()}
      </Box>


      {/* Iterate Dialog */}
      <Dialog
        open={showIterateDialog}
        onClose={() => setShowIterateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Iterate Prompt with Improvement Agent</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select an agent type to refine your prompt further. Each agent specializes in different aspects of prompt improvement.
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Agent Type</InputLabel>
              <Select
                value={selectedAgentType}
                onChange={(e) => setSelectedAgentType(e.target.value as any)}
                label="Agent Type"
              >
                <MenuItem value="Expander">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ExpandMoreIcon />
                    <Box>
                      <Typography variant="body1">Expander</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Adds detail and richness to prompts
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="Structurer">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StructureIcon />
                    <Box>
                      <Typography variant="body1">Structurer</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Organizes and clarifies prompt structure
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="Creative">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreativeIcon />
                    <Box>
                      <Typography variant="body1">Creative</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Adds creative flair and uniqueness
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="Critic">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CriticIcon />
                    <Box>
                      <Typography variant="body1">Critic</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Refines and sharpens prompt clarity
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Instructions (optional)"
              value={iterateInstructions}
              onChange={(e) => setIterateInstructions(e.target.value)}
              placeholder="Add specific guidance for the agent..."
              sx={{ mb: 3 }}
            />
            
            <Box>
              <Typography gutterBottom>Temperature: {iterateTemperature}</Typography>
              <Slider
                value={iterateTemperature}
                onChange={(_, value) => setIterateTemperature(value as number)}
                min={0}
                max={1}
                step={0.1}
                marks
                valueLabelDisplay="auto"
              />
              <FormHelperText>Higher values make the output more creative</FormHelperText>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIterateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePromptImproveIterate}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
          >
            {isLoading ? 'Iterating...' : 'Iterate Prompt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PromptImprovement;